import axios from 'axios'

/**
 * 数据服务类
 * 负责获取和缓存届次数据
 */
class DataService {
  constructor() {
    // 使用 Map 存储缓存数据
    this.cache = new Map()
    
    // API 基础 URL（从环境变量读取，默认为开发环境地址）
    this.baseURL = process.env.VUE_APP_API_BASE_URL || 'http://localhost:3000/api/v1/sports-data'
    
    // 请求超时时间（10秒）
    this.timeout = 10000
    
    // 创建 axios 实例
    this.axiosInstance = axios.create({
      baseURL: this.baseURL,
      timeout: this.timeout,
      headers: {
        'Content-Type': 'application/json'
      }
    })
  }

  /**
   * 获取指定届次的数据
   * @param {number|string} session - 届次编号 (1-5) 或 'all'
   * @returns {Promise<Object>} 届次数据对象
   */
  async getSessionData(session) {
    // 检查缓存
    if (this.cache.has(session)) {
      console.log(`从缓存获取第 ${session} 届数据`)
      return this.cache.get(session)
    }

    try {
      // 构建 API 端点
      const endpoint = session === 'all' ? '/session/all' : `/session/${session}`
      
      console.log(`正在从 API 获取第 ${session} 届数据: ${this.baseURL}${endpoint}`)
      
      // 发送请求
      const response = await this.axiosInstance.get(endpoint)
      
      const data = response.data
      
      // 验证数据结构
      if (!this.validateSessionData(data)) {
        console.error('API 返回的数据格式不正确:', data)
        throw new Error('API 返回的数据格式不正确')
      }
      
      console.log(`成功获取第 ${session} 届数据`)
      
      // 缓存数据
      this.cache.set(session, data)
      
      return data
    } catch (error) {
      console.error(`获取第 ${session} 届数据失败:`, error)
      
      // 处理超时错误
      if (error.code === 'ECONNABORTED') {
        throw new Error('请求超时，请检查网络连接或后端服务是否正常')
      }
      
      // 处理网络错误
      if (error.message === 'Network Error') {
        throw new Error('网络错误，请检查后端服务是否启动（http://localhost:3000）')
      }
      
      // 处理 HTTP 错误
      if (error.response) {
        const status = error.response.status
        const message = error.response.data?.error || error.response.data?.message
        
        if (status === 404) {
          throw new Error(`第 ${session} 届数据不存在`)
        } else if (status === 500) {
          throw new Error(`服务器错误: ${message || '请联系管理员'}`)
        } else {
          throw new Error(message || `HTTP ${status} 错误`)
        }
      }
      
      // 处理其他错误
      throw new Error(error.message || '获取数据失败')
    }
  }

  /**
   * 验证届次数据结构的完整性
   * @param {Object} data - 届次数据对象
   * @returns {boolean} 数据是否有效
   */
  validateSessionData(data) {
    if (!data || typeof data !== 'object') {
      return false
    }

    // 检查所有必需字段
    const requiredFields = [
      'overview',
      'projectDistribution',
      'projectList',
      'cityData',
      'cityBarData',
      'monthlyData',
      'venueScrollData',
      'crowdDistribution'
    ]

    return requiredFields.every(field => {
      const hasField = data.hasOwnProperty(field)
      if (!hasField) {
        console.warn(`缺少必需字段: ${field}`)
      }
      return hasField
    })
  }

  /**
   * 清除所有缓存数据
   */
  clearCache() {
    console.log('清除所有缓存数据')
    this.cache.clear()
  }

  /**
   * 清除指定届次的缓存
   * @param {number|string} session - 届次编号
   */
  clearSessionCache(session) {
    console.log(`清除第 ${session} 届缓存数据`)
    this.cache.delete(session)
  }
}

// 导出单例实例
export default new DataService()
